import { GlIcon, GlAlert, GlTooltipDirective } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { ERROR_POLICY_ALL } from '../../../lib/graphql.js?vue3';
import { s__, __, sprintf } from '~/locale';
import workItemByIidQuery from '~/work_items/graphql/work_item_by_iid.query.graphql';
import namespaceMergeRequestsEnabledQuery from '~/work_items/graphql/namespace_merge_requests_enabled.query.graphql';
import workItemDevelopmentQuery from '~/work_items/graphql/work_item_development.query.graphql';
import workItemDevelopmentUpdatedSubscription from '~/work_items/graphql/work_item_development.subscription.graphql';
import { DEVELOPMENT_ITEMS_ANCHOR, STATE_OPEN } from '~/work_items/constants';
import { findDevelopmentWidget, lowercaseWorkItemType } from '../../utils.js?vue3';
import CrudComponent from '../../../vue_shared/components/crud_component.vue?vue3';
import WorkItemActionsSplitButton from '../work_item_links/work_item_actions_split_button.vue?vue3';
import WorkItemDevelopmentRelationshipList from './work_item_development_relationship_list.vue?vue3';
import WorkItemCreateBranchMergeRequestModal from './work_item_create_branch_merge_request_modal.vue?vue3';
export default {
    name: 'WorkItemDevelopment',
    components: {
        GlIcon,
        GlAlert,
        WorkItemDevelopmentRelationshipList,
        CrudComponent,
        WorkItemActionsSplitButton,
        WorkItemCreateBranchMergeRequestModal
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    props: {
        workItemFullPath: {
            type: String,
            required: true
        },
        workItemId: {
            type: String,
            required: true
        },
        workItemIid: {
            type: String,
            required: false,
            default: null
        }
    },
    data () {
        return {
            error: undefined,
            mergeRequestsEnabled: false,
            workItem: {},
            workItemDevelopment: {},
            showModal: false,
            showBranchFlow: true,
            canCreateBranch: true
        };
    },
    computed: {
        itemCount () {
            return this.relatedMergeRequests.length + this.relatedBranches.length + this.featureFlags.length;
        },
        canUpdate () {
            return this.workItem?.userPermissions?.updateWorkItem;
        },
        workItemState () {
            return this.workItem?.state;
        },
        workItemType () {
            return this.workItem?.workItemType?.name;
        },
        isLoading () {
            return this.$apollo.queries.workItem.loading || this.$apollo.queries.workItemDevelopment.loading;
        },
        willAutoCloseByMergeRequest () {
            return this.workItemDevelopment?.willAutoCloseByMergeRequest;
        },
        relatedMergeRequests () {
            return this.workItemDevelopment?.relatedMergeRequests?.nodes || [];
        },
        closingMergeRequests () {
            return this.workItemDevelopment?.closingMergeRequests?.nodes || [];
        },
        featureFlags () {
            return this.workItemDevelopment?.featureFlags?.nodes || [];
        },
        relatedBranches () {
            return this.workItemDevelopment?.relatedBranches?.nodes || [];
        },
        shouldShowDevWidget () {
            return this.error || !this.isRelatedDevelopmentListEmpty;
        },
        isRelatedDevelopmentListEmpty () {
            return this.relatedMergeRequests.length === 0 && this.closingMergeRequests.length === 0 && this.featureFlags.length === 0 && this.relatedBranches.length === 0;
        },
        showAutoCloseInformation () {
            return this.closingMergeRequests.length > 0 && this.willAutoCloseByMergeRequest && !this.isLoading;
        },
        openStateText () {
            return this.closingMergeRequests.length > 1 ? sprintf(s__('WorkItem|This %{workItemType} will be closed when any of the following is merged.'), {
                workItemType: lowercaseWorkItemType(this.workItemType)
            }) : sprintf(s__('WorkItem|This %{workItemType} will be closed when the following is merged.'), {
                workItemType: lowercaseWorkItemType(this.workItemType)
            });
        },
        closedStateText () {
            return sprintf(s__('WorkItem|The %{workItemType} was closed automatically when a branch was merged.'), {
                workItemType: lowercaseWorkItemType(this.workItemType)
            });
        },
        tooltipText () {
            return this.workItemState === STATE_OPEN ? this.openStateText : this.closedStateText;
        },
        showAddButton () {
            return this.canUpdate && this.canCreateBranch;
        },
        isConfidentialWorkItem () {
            return this.workItem?.confidential;
        },
        projectId () {
            return this.workItem?.project?.id;
        },
        addItemsActions () {
            const actions = [];
            if (this.mergeRequestsEnabled) {
                actions.push({
                    name: __('Merge request'),
                    items: [
                        {
                            text: __('Create merge request'),
                            action: this.openModal.bind(this, false, true),
                            extraAttrs: {
                                'data-testid': 'create-mr-dropdown-button'
                            }
                        }
                    ]
                });
            }
            return actions.concat({
                name: __('Branch'),
                items: [
                    {
                        text: __('Create branch'),
                        action: this.openModal.bind(this, true, false),
                        extraAttrs: {
                            'data-testid': 'create-branch-dropdown-button'
                        }
                    }
                ]
            });
        }
    },
    apollo: {
        workItem: {
            query: workItemByIidQuery,
            variables () {
                return {
                    fullPath: this.workItemFullPath,
                    iid: this.workItemIid,
                    useWorkItemFeatures: true
                };
            },
            update (data) {
                return data.namespace?.workItem || {};
            },
            skip () {
                return !this.workItemIid;
            },
            error (error) {
                this.error = s__('WorkItem|Something went wrong when fetching items. Please refresh this page.');
                Sentry.captureException(error);
            }
        },
        workItemDevelopment: {
            query: workItemDevelopmentQuery,
            variables () {
                return {
                    id: this.workItemId,
                    useWorkItemFeatures: true
                };
            },
            update (data) {
                return findDevelopmentWidget(data?.workItem) || {};
            },
            skip () {
                return !this.workItemIid;
            },
            errorPolicy: ERROR_POLICY_ALL,
            error (error) {
                this.error = s__("WorkItem|One or more items cannot be shown. If you're using SAML authentication, this could mean your session has expired.");
                Sentry.captureException(error);
            },
            subscribeToMore: {
                document: workItemDevelopmentUpdatedSubscription,
                variables () {
                    return {
                        id: this.workItem.id,
                        useWorkItemFeatures: true
                    };
                },
                skip () {
                    return !this.workItem?.id;
                }
            }
        },
        mergeRequestsEnabled: {
            query: namespaceMergeRequestsEnabledQuery,
            variables () {
                return {
                    fullPath: this.workItemFullPath
                };
            },
            update (data) {
                return data.namespace?.mergeRequestsEnabled ?? false;
            },
            skip () {
                return !this.workItemFullPath;
            },
            error (error) {
                Sentry.captureException(error);
            }
        }
    },
    methods: {
        openModal (createBranch = true) {
            this.toggleCreateModal(true);
            this.showBranchFlow = createBranch;
        },
        toggleCreateModal (showModal) {
            this.showModal = showModal;
        },
        updatePermissions (canCreateBranch) {
            this.canCreateBranch = canCreateBranch;
        }
    },
    DEVELOPMENT_ITEMS_ANCHOR
};
